/**
 * What was actually agreed: the lines, the milestones on each, and the payments they trigger.
 *
 * EVERYTHING DATED IS COMPUTED AND STORED NOWHERE, and this pane shows the computed answers rather
 * than recomputing any of them. There is no OVERDUE column on a payment and no EXPIRED on a line:
 * a stored OVERDUE is wrong the morning after it is written unless something sweeps it, and a
 * sweep that fails quietly leaves a schedule that looks current. Due dates, lateness, resolved
 * amounts and what a late fee or early-payment discount WOULD come to are all worked out on every
 * read, by the service, once.
 *
 * AN UNKNOWN AMOUNT IS NOT ZERO, AND THE PANE REFUSES TO PRINT IT AS ONE. A payment that is a
 * percentage of a framework line -- a line that commits no quantity -- has no resolvable amount at
 * all. Showing "0.00" there would put a number in a column that sums, and somebody would add it up.
 * It says "not known" and carries the service's own reason for why.
 *
 * SO THE TOTALS SAY WHAT THEY DO NOT COVER. A scheduled total of 40,000 means something different
 * when three payments on it could not be resolved, and the count travels beside the money for
 * exactly that reason -- the same rule as the supplier expiry report's third answer.
 *
 * THERE IS NO VERDICT, DELIBERATELY. The service returns findings and no overall status, because
 * "this contract is fine" is not a statement anything here can make: a late milestone, an expired
 * price and an over-allocated line are different problems for different people. The findings ARE
 * the answer, so they are listed and never reduced to a colour.
 *
 * A LATE FEE IS ARITHMETIC, NOT A CHARGE. `LateFeeWouldBe` is what the recorded terms would come to
 * if somebody chose to apply them; nothing here posts it and nothing invoices it, and the wording
 * says so wherever it appears.
 *
 * READ-ONLY, AND THAT IS A SCOPE DECISION RATHER THAN A RULE. Adding a line, a milestone or a
 * payment is a real surface with its own refusals, and this panel is the composition that had
 * none at all -- the schedule itself. The service's write actions exist; the screen for them does
 * not yet, and this says so rather than offering buttons that are not there.
 */
import React, {useCallback, useContext, useEffect, useState} from "react";
import {Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
        Tooltip, Typography} from "@mui/material";
import FormRenderer from "../Forms/FormRenderer";

import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Blue3, Grey2, Grey5, Grey7, Grey8, Grey17} from "../../theme/styles";
import {money} from "./QuoteLinesPane";

type Milestone = {
    USID: string; Name?: string | null; Description?: string | null;
    TargetDate?: string | null; CompletedDate?: string | null;
    Status?: string | null; Timeliness?: string | null; DaysLate?: number | null;
};

type Payment = {
    USID: string; Name?: string | null; Trigger?: string | null;
    MilestoneName?: string | null; Amount?: string | null; Percentage?: string | null;
    ResolvedAmount?: string | null; AmountKnown?: boolean;
    AmountUnknownBecause?: string | null; Currency?: string | null;
    DueDate?: string | null; DueDateSource?: string | null;
    ProjectedDueDate?: string | null; Status?: string | null;
    IsOverdue?: boolean; DaysOverdue?: number | null;
    PaidDate?: string | null; PaidAmount?: string | null;
    LateFeeWouldBe?: string | null; EarlyPaymentDiscountWouldBe?: string | null;
    InvoiceReference?: string | null;
};

type Line = {
    USID: string; Description?: string | null; AgreedPrice?: string | null;
    UnitOfMeasure?: string | null; Quantity?: string | null; CommitsQuantity?: boolean;
    CommittedValue?: string | null; CommittedValueUnknownBecause?: string | null;
    Currency?: string | null; ExpiryDate?: string | null; PercentageAllocated?: string | null;
    ListOfContractLineMilestone?: {ContractLineMilestone?: Milestone[] | Milestone};
    ListOfContractLinePayment?: {ContractLinePayment?: Payment[] | Payment};
};

type Finding = {Finding?: string | null; Subject?: string | null; Why?: string | null};

type Summary = {
    Currency?: string | null; Lines?: number; CommittedValue?: string | null;
    LinesWithNoCommittedQuantity?: number; StatedContractValue?: string | null;
    Variance?: string | null; ScheduledPayments?: string | null; PaidPayments?: string | null;
    OutstandingPayments?: string | null; OverduePayments?: number;
    OverdueAmount?: string | null; PaymentsWithUnknownAmount?: number;
    TaxOnCommittedValue?: string | null; TaxTreatment?: string | null;
};

function listOf<T>(held: T[] | T | undefined | null): T[] {
    if (held === null || held === undefined) return [];
    return Array.isArray(held) ? held : [held];
}

function when(value?: string | null): string {
    if (!value) return "—";
    const at = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(at.getTime())) return String(value).slice(0, 10);
    return at.toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
}

function spell(value?: string | null): string {
    const text = String(value ?? "").replace(/_/g, " ").toLowerCase();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : "—";
}

// THE SAME PRIVILEGE THE OTHER FILED-IN-PROJECT KINDS TAKE. The five classifications a project
// files were folded under project:* on 2026-10-27; the quote and build-plan panes say the same.
const EDIT_PRIVILEGE = "project:edit";

export default function ContractSchedulePane(
    {revisionUSID, status}: {revisionUSID?: string | null; status?: string | null}) {

    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [summary, setSummary] = useState<Summary | null>(null);
    const [terms, setTerms] = useState<any>(null);
    const [lines, setLines] = useState<Line[]>([]);
    const [findings, setFindings] = useState<Finding[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // WHAT A NEW LINE IS ASKED WITH: the realm's CONTRACT_LINE_FORM composite, named by the
    // schedule read, or null where the seed has not been applied -- said, not drawn blank.
    const [lineShape, setLineShape] = useState<string | null>(null);
    const [mayEdit, setMayEdit] = useState(false);
    const [adding, setAdding] = useState(false);
    const [removing, setRemoving] = useState<string | null>(null);
    const [removeProblem, setRemoveProblem] = useState<string | null>(null);

    const load = useCallback(() => {
        if (!revisionUSID) return;
        setLoading(true);
        api.get("t1/v1/contract_schedules", {params: {ContractRevisionUSID: revisionUSID}})
            .then((response: any) => {
                const message = response?.data?.PlatformMessage ?? {};
                setSummary(message?.Summary ?? null);
                setTerms(message?.Terms ?? null);
                setLines(listOf<Line>(message?.ListOfContractLine?.ContractLine));
                setFindings(listOf<Finding>(message?.ListOfFinding?.Finding));
                setLineShape(message?.LineShapeRevisionUSID ?? null);
                setError(null);
            })
            .catch((problem: any) => {
                setSummary(null);
                // A CONTRACT WITH NO TERMS IS A LEGITIMATE RECORD -- a quality agreement has no
                // money in it -- and the service says exactly that. Shown as written rather than
                // dressed as a failure.
                setError(problem?.response?.data?.Cause
                         ?? "This contract's schedule could not be read.");
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revisionUSID]);

    useEffect(() => { load(); }, [load]);

    // ASKED OF THE RECORD, NOT OF THE SESSION -- the same read the quote pane makes, for the same
    // reason: a per-share grant or a deny narrows privileges for ONE contract, and a global answer
    // would offer an add the server refuses after the dialog has been filled in.
    useEffect(() => {
        if (!revisionUSID) return;
        let cancelled = false;
        api.get("t1/v1/object_access", {params: {USID: revisionUSID}})
            .then((response: any) => {
                if (cancelled) return;
                const granted: string[] = response?.data?.EffectivePrivileges ?? [];
                setMayEdit(granted.includes(EDIT_PRIVILEGE));
            })
            .catch(() => { if (!cancelled) setMayEdit(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revisionUSID]);

    // LINES ARE PART OF WHAT WAS AGREED, so they change only on a DRAFT; the server refuses the
    // rest (CC-CTLN-FROZEN) and this does not offer what it would refuse. An unknown status -- a
    // host that did not pass one -- offers the control and lets the server answer.
    const editable = mayEdit && (status == null || String(status).toUpperCase() === "DRAFT");

    const remove = (line: Line) => {
        setRemoving(line.USID);
        setRemoveProblem(null);
        api.delete("t1/v1/contract_lines", {params: {USID: line.USID}})
            .then(() => load())
            .catch((problem: any) => setRemoveProblem(
                problem?.response?.data?.Cause ?? "The line could not be removed."))
            .finally(() => setRemoving(null));
    };

    if (loading && !summary && !error) {
        return <Box sx={{display: "flex", gap: "10px", alignItems: "center", padding: "20px 0"}}>
            <CircularProgress size={18}/>
            <Typography sx={{fontSize: 13, color: Grey7}}>Working out the schedule…</Typography>
        </Box>;
    }

    if (error) {
        return <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.7, maxWidth: 760,
                                paddingTop: "8px"}}>{error}</Typography>;
    }

    if (!summary) return null;

    const currency = summary.Currency ?? null;
    const places = currency === "JPY" ? 0 : 2;

    return (
        <Box sx={{paddingTop: "6px", maxWidth: 1020}}>

            {/* ── what was agreed ───────────────────────────────────────────────────────────── */}
            <Box sx={{display: "flex", gap: "28px", flexWrap: "wrap",
                      border: `1px solid ${Grey2}`, borderRadius: "6px",
                      padding: "12px 16px", marginBottom: "18px"}}>
                <Fact label="Currency" value={currency ?? "—"}/>
                <Fact label="Lines" value={summary.Lines ?? 0}/>
                <Fact label="Committed value"
                      value={money(summary.CommittedValue, currency, places)}/>
                {summary.StatedContractValue != null && (
                    <>
                        <Fact label="Stated value"
                              value={money(summary.StatedContractValue, currency, places)}/>
                        {/* THE VARIANCE IS WHY BOTH ARE SHOWN. A contract whose lines come to a
                            different number from the figure written on its face is a thing
                            somebody has to explain, and neither number alone says it. */}
                        <Fact label="Variance"
                              value={<span style={{color: summary.Variance
                                                          && !String(summary.Variance)
                                                               .startsWith("0")
                                                          ? "#B26A00" : Grey17}}>
                                  {money(summary.Variance, currency, places)}
                              </span>}/>
                    </>
                )}
                {summary.TaxOnCommittedValue != null && (
                    <Fact label={`Tax (${spell(summary.TaxTreatment)})`}
                          value={money(summary.TaxOnCommittedValue, currency, places)}/>
                )}
            </Box>

            {/* ── the money, and what the money does not cover ──────────────────────────────── */}
            <Box sx={{display: "flex", gap: "28px", flexWrap: "wrap",
                      border: `1px solid ${Grey2}`, borderRadius: "6px",
                      padding: "12px 16px", marginBottom: "10px"}}>
                <Fact label="Scheduled" value={money(summary.ScheduledPayments, currency, places)}/>
                <Fact label="Paid" value={money(summary.PaidPayments, currency, places)}/>
                <Fact label="Outstanding"
                      value={money(summary.OutstandingPayments, currency, places)}/>
                <Fact label="Overdue"
                      value={<span style={{color: summary.OverduePayments ? "#B3261E" : Grey17}}>
                          {summary.OverduePayments
                              ? `${money(summary.OverdueAmount, currency, places)} `
                                + `(${summary.OverduePayments})`
                              : "none"}
                      </span>}/>
            </Box>

            {/* SAY WHAT THE TOTALS DO NOT COVER. A scheduled total means something different when
                three payments on it could not be resolved at all. */}
            {(!!summary.PaymentsWithUnknownAmount || !!summary.LinesWithNoCommittedQuantity) && (
                <Box sx={{display: "flex", flexDirection: "column", gap: "4px",
                          marginBottom: "18px"}}>
                    {!!summary.PaymentsWithUnknownAmount && (
                        <Typography sx={{fontSize: 13, color: "#B26A00", lineHeight: 1.6}}>
                            {summary.PaymentsWithUnknownAmount === 1
                                ? "1 payment has no resolvable amount, so it is in none of these "
                                  + "totals."
                                : `${summary.PaymentsWithUnknownAmount} payments have no resolvable `
                                  + "amount, so they are in none of these totals."}
                        </Typography>
                    )}
                    {!!summary.LinesWithNoCommittedQuantity && (
                        <Typography sx={{fontSize: 13, color: "#B26A00", lineHeight: 1.6}}>
                            {summary.LinesWithNoCommittedQuantity === 1
                                ? "1 line commits no quantity — a framework price, not a committed "
                                  + "value."
                                : `${summary.LinesWithNoCommittedQuantity} lines commit no quantity `
                                  + "— framework prices, not committed values."}
                        </Typography>
                    )}
                </Box>
            )}

            {/* ── the findings, which ARE the answer ────────────────────────────────────────── */}
            <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8, marginBottom: "8px"}}>
                Findings
            </Typography>
            {findings.length === 0 ? (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 820,
                                 marginBottom: "18px"}}>
                    Nothing on this schedule is late, lapsed or over-allocated today.
                </Typography>
            ) : (
                <Box sx={{marginBottom: "18px"}}>
                    {findings.map((one, index) => (
                        <Box key={`${one.Finding}-${index}`}
                             sx={{display: "flex", gap: "10px", alignItems: "baseline",
                                  padding: "6px 0", borderBottom: `1px solid ${Grey2}`}}>
                            <Chip size="small" label={spell(one.Finding)}
                                  sx={{height: 20, fontSize: 11,
                                       background: one.Finding === "PAYMENT_OVERDUE"
                                                   || one.Finding === "MILESTONE_LATE"
                                                   ? "#FCEBEA" : "#FDF3E3",
                                       color: one.Finding === "PAYMENT_OVERDUE"
                                              || one.Finding === "MILESTONE_LATE"
                                              ? "#B3261E" : "#B26A00"}}/>
                            <Typography sx={{fontSize: 13, color: Grey17, lineHeight: 1.6}}>
                                <strong>{one.Subject}</strong> — {one.Why}
                            </Typography>
                        </Box>
                    ))}
                    {/* NO VERDICT, SAID ONCE. A green tick over these would be a claim nothing
                        here can make. */}
                    <Typography sx={{fontSize: 12, color: Grey5, lineHeight: 1.6,
                                     marginTop: "8px", maxWidth: 820}}>
                        There is no overall status: a late milestone, a lapsed price and an
                        over-allocated line are different problems for different people, and a
                        single colour over them would hide which.
                    </Typography>
                </Box>
            )}

            {/* ── the lines, each with its milestones and payments ──────────────────────────── */}
            <Box sx={{display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px"}}>
                <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8}}>
                    Lines
                </Typography>
                <Box sx={{flex: 1}}/>
                {/* THE LINE ITEMS, AS THE SOURCE PRODUCT ADDS THEM: Item, Agreed Price per unit,
                    Currency, Per Line Expiry, Committed Qty, Unit, Notes. Asked through the FORM
                    ENGINE -- Ed, 2026-10-28: "There needs to be a form element for items" -- so
                    the Item is a form element resolving the Item classification and a tenant can
                    reshape the questions like any form. */}
                {editable && (
                    <Button size="small" variant="outlined" sx={{textTransform: "none"}}
                            onClick={() => setAdding(true)}>Add line item</Button>
                )}
            </Box>
            {removeProblem && (
                <Typography sx={{fontSize: 13, color: "#B3261E", marginBottom: "8px"}}>
                    {removeProblem}
                </Typography>
            )}
            {lines.length === 0 ? (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 820}}>
                    This agreement has no lines, so there is nothing to schedule against.
                </Typography>
            ) : lines.map(line => {
                const milestones = listOf<Milestone>(
                    line.ListOfContractLineMilestone?.ContractLineMilestone);
                const payments = listOf<Payment>(
                    line.ListOfContractLinePayment?.ContractLinePayment);
                const lineCurrency = line.Currency ?? currency;
                return (
                    <Box key={line.USID}
                         sx={{border: `1px solid ${Grey2}`, borderRadius: "6px",
                              padding: "12px 14px", marginBottom: "12px"}}>
                        <Box sx={{display: "flex", gap: "12px", alignItems: "baseline",
                                  flexWrap: "wrap"}}>
                            <Typography sx={{fontSize: 13, color: Grey17, fontWeight: 600}}>
                                {line.Description || "—"}
                            </Typography>
                            <Box sx={{flex: 1}}/>
                            {editable && (
                                <Typography sx={{fontSize: 12.5, color: Grey7, cursor: "pointer"}}
                                            onClick={() => removing !== line.USID && remove(line)}>
                                    {removing === line.USID ? "Removing…" : "Remove"}
                                </Typography>
                            )}
                            {/* A FRAMEWORK LINE HAS A PRICE AND NO COMMITTED VALUE, and the
                                service says why. Printing 0.00 would invite somebody to add it up. */}
                            <Typography sx={{fontSize: 13, color: line.CommittedValue == null
                                                                 ? "#B26A00" : Grey17,
                                             fontVariantNumeric: "tabular-nums"}}>
                                {line.CommittedValue == null
                                    ? "value not known"
                                    : money(line.CommittedValue, lineCurrency, places)}
                            </Typography>
                        </Box>
                        <Box sx={{display: "flex", gap: "22px", flexWrap: "wrap",
                                  marginTop: "6px"}}>
                            <Small label="Agreed price"
                                   value={money(line.AgreedPrice, lineCurrency, places)}/>
                            <Small label="Quantity"
                                   value={line.CommitsQuantity && line.Quantity
                                          ? `${line.Quantity}${line.UnitOfMeasure
                                                               ? ` ${line.UnitOfMeasure}` : ""}`
                                          : "framework — none committed"}/>
                            {line.ExpiryDate && (
                                <Small label="Price lapses" value={when(line.ExpiryDate)}/>
                            )}
                            {line.PercentageAllocated != null
                             && Number(line.PercentageAllocated) > 0 && (
                                <Small label="Allocated"
                                       value={`${line.PercentageAllocated}%`}/>
                            )}
                        </Box>
                        {line.CommittedValueUnknownBecause && (
                            <Typography sx={{fontSize: 12, color: Grey5, lineHeight: 1.6,
                                             marginTop: "6px", maxWidth: 820}}>
                                {line.CommittedValueUnknownBecause}
                            </Typography>
                        )}

                        {!!milestones.length && (
                            <Box sx={{marginTop: "10px"}}>
                                <Typography sx={{fontSize: 11, color: Grey5,
                                                 textTransform: "uppercase",
                                                 letterSpacing: "0.04em", marginBottom: "4px"}}>
                                    Milestones
                                </Typography>
                                {milestones.map(one => (
                                    <Box key={one.USID}
                                         sx={{display: "flex", gap: "10px", alignItems: "baseline",
                                              padding: "3px 0"}}>
                                        <Typography sx={{fontSize: 13, color: Grey17, flex: 1}}>
                                            {one.Name}
                                        </Typography>
                                        <Typography sx={{fontSize: 12, color: Grey5}}>
                                            target {when(one.TargetDate)}
                                        </Typography>
                                        <Typography sx={{fontSize: 12,
                                                         color: one.Timeliness === "LATE"
                                                                ? "#B3261E"
                                                                : one.Timeliness === "ON_TIME"
                                                                    ? "#1E7B34" : Grey7,
                                                         minWidth: 110, textAlign: "right"}}>
                                            {one.Timeliness === "LATE" && one.DaysLate
                                                ? `${one.DaysLate} day`
                                                  + `${one.DaysLate === 1 ? "" : "s"} late`
                                                : spell(one.Status)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {!!payments.length && (
                            <Box sx={{marginTop: "10px"}}>
                                <Typography sx={{fontSize: 11, color: Grey5,
                                                 textTransform: "uppercase",
                                                 letterSpacing: "0.04em", marginBottom: "4px"}}>
                                    Payments
                                </Typography>
                                {payments.map(one => (
                                    <Box key={one.USID}
                                         sx={{display: "flex", gap: "10px", alignItems: "baseline",
                                              padding: "3px 0", flexWrap: "wrap"}}>
                                        <Typography sx={{fontSize: 13, color: Grey17, flex: 1}}>
                                            {one.Name}
                                            {one.Percentage
                                                ? ` — ${one.Percentage}% of the line`
                                                : ""}
                                        </Typography>
                                        {/* THE DUE DATE MAY BE PROJECTED rather than fixed, and
                                            which one it is changes what a reader should do. */}
                                        <Typography sx={{fontSize: 12, color: Grey5}}>
                                            {one.DueDate
                                                ? `due ${when(one.DueDate)}`
                                                : one.ProjectedDueDate
                                                    ? `projected ${when(one.ProjectedDueDate)}`
                                                    : "no due date"}
                                        </Typography>
                                        {one.AmountKnown === false ? (
                                            <Tooltip title={one.AmountUnknownBecause
                                                             ?? "The amount cannot be resolved."}>
                                                <Typography sx={{fontSize: 13, color: "#B26A00",
                                                                 minWidth: 120,
                                                                 textAlign: "right"}}>
                                                    not known
                                                </Typography>
                                            </Tooltip>
                                        ) : (
                                            <Typography sx={{fontSize: 13, color: Grey17,
                                                             minWidth: 120, textAlign: "right",
                                                             fontVariantNumeric: "tabular-nums"}}>
                                                {money(one.ResolvedAmount, lineCurrency, places)}
                                            </Typography>
                                        )}
                                        <Typography sx={{fontSize: 12, minWidth: 90,
                                                         textAlign: "right",
                                                         color: one.IsOverdue ? "#B3261E"
                                                                : one.Status === "PAID"
                                                                    ? "#1E7B34" : Grey7}}>
                                            {one.IsOverdue && one.DaysOverdue
                                                ? `${one.DaysOverdue} day`
                                                  + `${one.DaysOverdue === 1 ? "" : "s"} overdue`
                                                : spell(one.Status)}
                                        </Typography>
                                    </Box>
                                ))}
                                {/* ARITHMETIC, NOT A CHARGE. Nothing here posts or invoices it,
                                    and the wording has to say so or somebody will assume it did. */}
                                {payments.some(one => one.LateFeeWouldBe) && (
                                    <Typography sx={{fontSize: 12, color: Grey5, lineHeight: 1.6,
                                                     marginTop: "6px"}}>
                                        Late fees shown anywhere on this page are what the recorded
                                        terms would come to if somebody applied them. Nothing here
                                        charges or invoices anything.
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                );
            })}

            <Typography sx={{fontSize: 12, color: Grey5, lineHeight: 1.6, marginTop: "16px",
                             maxWidth: 820}}>
                Every date, amount and lateness on this page is worked out when the page is read —
                none of it is stored, so none of it can go stale. Adding or changing a line, a
                milestone or a payment is not on this screen yet.
            </Typography>
            {adding && revisionUSID && (
                <AddLineItemDialog revisionUSID={revisionUSID} shape={lineShape}
                                   currency={terms?.BillingCurrency ?? null}
                                   onClose={() => setAdding(false)}
                                   onAdded={() => { setAdding(false); load(); }}/>
            )}
        </Box>
    );
}

function Fact({label, value}: {label: string; value: React.ReactNode}) {
    return (
        <Box>
            <Typography sx={{fontSize: 11, color: Grey5, textTransform: "uppercase",
                             letterSpacing: "0.04em"}}>
                {label}
            </Typography>
            <Typography sx={{fontSize: 15, color: Blue3, marginTop: "2px", fontWeight: 600,
                             fontVariantNumeric: "tabular-nums"}}>
                {value}
            </Typography>
        </Box>
    );
}

function Small({label, value}: {label: string; value: React.ReactNode}) {
    return (
        <Box sx={{display: "flex", gap: "6px", alignItems: "baseline"}}>
            <Typography sx={{fontSize: 12, color: Grey5}}>{label}</Typography>
            <Typography sx={{fontSize: 12, color: Grey17}}>{value}</Typography>
        </Box>
    );
}


/**
 * Add Line Item -- the source product's dialog, asked through the form engine.
 *
 * THE QUESTIONS ARE THE REALM'S CONTRACT_LINE_FORM SHAPE (contract_form_seed.sql): Item -- a form
 * element resolving the Item classification -- Agreed Price per unit, Per Line Expiry, Committed
 * Qty, Unit, Notes. The currency is NOT asked: the terms carry it once, and it is shown beside the
 * price so the number is read in the right money. The answers land in raw.CONTRACT_LINE through
 * Add Contract Line, which is what the schedule arithmetic reads.
 *
 * NO SHAPE, SAID SO. A realm the seed has not reached draws a sentence naming the seed rather than
 * an empty dialog that looks like a broken one.
 */
function AddLineItemDialog({revisionUSID, shape, currency, onClose, onAdded}: {
    revisionUSID: string; shape: string | null; currency: string | null;
    onClose: () => void; onAdded: () => void;
}) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);
    const [values, setValues] = useState<Record<string, any>>({});
    const [problems, setProblems] = useState<any[]>([]);
    const [problem, setProblem] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const blank = (value: any) => value == null || String(value).trim() === "";
    const ready = !blank(values.item) && !blank(values.agreedPrice) && !blank(values.unitOfMeasure);

    const save = () => {
        setSaving(true);
        setProblem(null);
        api.post("t1/v1/contract_lines", {
            ContractRevisionUSID: revisionUSID,
            ItemObjectUSID: values.item,
            AgreedPrice: values.agreedPrice,
            UnitOfMeasure: values.unitOfMeasure,
            // LEFT OUT MEANS "NO COMMITTED QUANTITY", which is not zero; the service refuses zero.
            Quantity: blank(values.quantity) ? undefined : values.quantity,
            ExpiryDate: blank(values.expiryDate) ? undefined : values.expiryDate,
            Notes: blank(values.notes) ? undefined : values.notes,
        })
            .then(() => onAdded())
            .catch((failure: any) => {
                setProblems(failure?.response?.data?.Problems ?? []);
                setProblem(failure?.response?.data?.Cause ?? failure?.response?.data?.Code
                           ?? "The line could not be added.");
            })
            .finally(() => setSaving(false));
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 16}}>Add line item</DialogTitle>
            <DialogContent>
                <Box sx={{display: "flex", flexDirection: "column", gap: "10px", paddingTop: "4px"}}>
                    <Typography sx={{fontSize: 12.5, color: Grey7}}>
                        Currency: <span style={{color: Grey17, fontWeight: 500}}>
                            {currency ?? "not set — Set Contract Terms names it"}
                        </span> — from the contract's terms; every line is priced in it.
                    </Typography>
                    {shape ? (
                        <FormRenderer compositeRevisionUSID={shape} value={values}
                                      onChange={setValues} problems={problems}/>
                    ) : (
                        <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6}}>
                            This account has no line-item form yet. It is seeded per realm by
                            contract_form_seed.sql (the CONTRACT_LINE_FORM shape); until it is
                            applied here there is nothing to ask.
                        </Typography>
                    )}
                    {problem && (
                        <Typography sx={{fontSize: 13, color: "#B3261E", lineHeight: 1.5}}>
                            {problem}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose}>Cancel</Button>
                <Button size="small" variant="contained" disabled={!shape || !ready || saving}
                        onClick={save}>
                    {saving ? "Adding…" : "Add line item"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
